import { usePersistedState } from "@/lib/persistedFilters";
import { useEffect, useState, useCallback, Fragment, useRef, useMemo } from "react";
import { Search, ScrollText, VenetianMask, SlidersHorizontal, ChevronRight, ChevronDown, X, Download, Sparkles, Terminal, RefreshCcw, AlertTriangle, ShieldAlert, Play, Pause, Activity as ActivityIcon, Wallet, Database, UserCog, ArrowRight, Cloud, CalendarClock } from "lucide-react";
import { Link, useSearchParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { OAuthTracerPanel } from "@/pages/OAuthLiveDebug";
import { AiSpendPanel } from "@/components/AiSpendPanel";
import { AwsCloudTrailPanel } from "@/components/AwsCloudTrailPanel";
import { downloadText } from "@/lib/download";
import { toast } from "sonner";
import { PageHeader } from "@/components/PageHeader";
import AuditLogPage from "@/pages/AuditLogPage";
import { useAuth } from "@/context/AuthContext";
import { useTablePagination, TablePagination, PageSizeSelect } from "@/components/TablePagination";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuCheckboxItem, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

const CAT_COLORS = {
  login: "bg-[#eef2fb] text-primary", account: "bg-emerald-50 text-emerald-700",
  organization: "bg-amber-50 text-amber-700", permission: "bg-violet-50 text-violet-700",
  app: "bg-sky-50 text-sky-700", app_access: "bg-sky-50 text-sky-700", auth: "bg-rose-50 text-rose-700",
  inventory: "bg-teal-50 text-teal-700", quotes: "bg-indigo-50 text-indigo-700",
  settings: "bg-slate-100 text-slate-600", system: "bg-slate-100 text-slate-600", general: "bg-slate-100 text-slate-600",
  developer: "bg-fuchsia-50 text-fuchsia-700", request: "bg-orange-50 text-orange-700",
  security: "bg-red-50 text-red-700", kb: "bg-lime-50 text-lime-700",
};

const CAT_LABELS = { kb: "Knowledge Base", app_access: "App Access", auth: "Auth / Masquerade" };

const COLS = [
  { key: "actor", label: "Actor" },
  { key: "category", label: "Category" },
  { key: "partner", label: "Partner" },
  { key: "customer", label: "Customer" },
  { key: "app", label: "App" },
  { key: "details", label: "Details" },
  { key: "changes", label: "Changes" },
  { key: "time", label: "Time" },
];
const PREFS_KEY = "logs_cols_v1";

// Iter 117 — collapsible Activity sections. Collapse state is saved
// per-user server-side (table_prefs.activity_sections) so it survives
// logout/login and device changes.
const SECTION_DEFAULTS = { activity: true, audit: false, role_changes: false, aws_cloudtrail: false, system: true, oauth: false, ai_spend: false };

// Iter 119x — Deep links. `/activity?section=audit` expands that section on
// arrival (and scrolls to it) regardless of the saved collapse preference.
const SECTION_TESTIDS = {
  activity: "activity-section",
  audit: "phone-portal-audit-section",
  role_changes: "role-changes-section",
  aws_cloudtrail: "aws-cloudtrail-section",
  system: "system-section",
  oauth: "oauth-tracer-section",
  ai_spend: "ai-spend-section",
};

function CollapsibleSection({ title, subtitle, icon: Icon, open, onToggle, children, testid, badge, reportKey, canReport }) {
  return (
    <div className="mb-6" data-testid={testid}>
      <div className="flex items-stretch gap-2">
        <button
          type="button"
          data-testid={`${testid}-toggle`}
          onClick={onToggle}
          aria-expanded={open}
          className="flex w-full items-center justify-between rounded-lg border border-slate-200 bg-white px-4 py-3 text-left shadow-sm transition-colors hover:bg-slate-50"
        >
          <div className="flex items-center gap-2.5">
            <Icon className="h-4 w-4 text-primary" />
            <span className="font-heading text-sm font-semibold text-slate-800">{title}</span>
            {badge}
            {subtitle && <span className="hidden text-xs text-slate-400 sm:inline">— {subtitle}</span>}
          </div>
          <ChevronDown className={`h-4 w-4 shrink-0 text-slate-400 transition-transform ${open ? "" : "-rotate-90"}`} />
        </button>
        {/* Every Activity table can be generated, emailed or scheduled from the Reports hub. */}
        {reportKey && canReport && (
          <Link to={`/reports/${reportKey}`} data-testid={`${testid}-report-link`} title="Generate, email or schedule this table as a report"
                className="inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 text-xs font-medium text-slate-600 shadow-sm transition-colors hover:border-primary/40 hover:text-primary">
            <CalendarClock className="h-3.5 w-3.5" /> Report
          </Link>
        )}
      </div>
      {open && <div className="mt-3">{children}</div>}
    </div>
  );
}

export default function ActivityLogs() {
  const { has, user } = useAuth();
  const canViewActivityLogs = has("activity_logs.view");
  const canViewAuditLog = has("audit_log.view");
  const canViewSystemLogs = has("system_logs.view");
  const canViewRoleChanges = has("user_roles.view");
  const canViewCloudTrail = has("activity_logs.view") && has("aws_cloudtrail.view");
  const canReport = has("reports.view");
  const isSuper = user?.base_role === "super_admin" || user?.role === "super_admin";
  const [sections, setSections] = useState(null);   // null = prefs still loading
  const [searchParams] = useSearchParams();
  const deepSection = searchParams.get("section");
  const deepLinkApplied = useRef(false);

  useEffect(() => {
    api.get("/me/table-prefs?table=activity_sections")
      .then((r) => setSections({ ...SECTION_DEFAULTS, ...(r.data.prefs || {}) }))
      .catch(() => setSections({ ...SECTION_DEFAULTS }));
  }, []);

  // Iter 119x — Honour `?section=<key>` once the saved prefs have landed:
  // force that section open (persisting the choice, so the next visit
  // without the param behaves the same) and scroll it into view.
  useEffect(() => {
    if (!sections || !deepSection || deepLinkApplied.current) return;
    if (!(deepSection in SECTION_DEFAULTS)) return;
    deepLinkApplied.current = true;
    if (!sections[deepSection]) {
      const next = { ...sections, [deepSection]: true };
      setSections(next);
      api.put("/me/table-prefs", { table: "activity_sections", prefs: next }).catch(() => {});
    }
    const t = setTimeout(() => {
      document.querySelector(`[data-testid="${SECTION_TESTIDS[deepSection]}"]`)
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 350);
    return () => clearTimeout(t);
  }, [sections, deepSection]);

  const toggleSection = (k) => {
    const next = { ...sections, [k]: !sections[k] };
    setSections(next);
    api.put("/me/table-prefs", { table: "activity_sections", prefs: next }).catch(() => {});
  };
  const [logs, setLogs] = useState([]);
  const [search, setSearch] = usePersistedState("activity.search", "");
  const [category, setCategory] = usePersistedState("activity.category", "all");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  const [pending, setPending] = useState([]);   // buffered SSE rows waiting to be merged
  const [denied, setDenied] = useState(false);  // server refused the fetch (role changed mid-session)
  const filtersRef = useRef({ search: "", category: "all", start: "", end: "" });
  const [cols, setCols] = useState(() => {
    try { return JSON.parse(localStorage.getItem(PREFS_KEY)) || {}; } catch { return {}; }
  });

  const isVisible = (k) => cols[k] !== false;
  const toggleCol = (k) => setCols((c) => { const n = { ...c, [k]: c[k] === false ? true : false }; localStorage.setItem(PREFS_KEY, JSON.stringify(n)); return n; });

  const load = useCallback(async () => {
    // Iter 106c — Skip the audit-logs fetch entirely for operators
    // who don't hold `activity_logs.view` (they only reached this
    // page via `system_logs.view`). Prevents an unnecessary 403.
    if (!canViewActivityLogs) { setLoading(false); return; }
    setLoading(true);
    const params = {};
    if (search) params.search = search;
    if (category !== "all") params.category = category;
    if (start) params.start = start;
    if (end) params.end = end;
    try {
      const { data } = await api.get("/activity-logs", { params });
      setLogs(data);
      setDenied(false);
    } catch (e) {
      // Iter 275 — the browser's permission list is a snapshot taken at sign-in.
      // When an admin edits the user's role while they sit on this page, the
      // next fetch is refused and this used to surface as an uncaught promise
      // rejection in the error log. Say what happened instead.
      if (e?.response?.status === 403) {
        setDenied(true);
        setLogs([]);
      } else {
        toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not load the activity log.");
      }
    } finally { setLoading(false); }
  }, [search, category, start, end, canViewActivityLogs]);

  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [load]);

  // Keep a live filters ref so the SSE handler can drop non-matching rows
  // without becoming a dependency of the effect (which would tear down the
  // stream on every keystroke).
  useEffect(() => {
    filtersRef.current = { search, category, start, end };
    // When the filters change we're showing a different slice — clear the
    // pending buffer so the pill doesn't offer to insert stale rows.
    setPending([]);
  }, [search, category, start, end]);

  // Row-matches-current-filters check. Mirrors the backend `list_logs` query.
  const matchesFilters = (row) => {
    const f = filtersRef.current;
    if (f.category !== "all" && row.category !== f.category) return false;
    if (f.start && row.created_at && row.created_at < f.start) return false;
    if (f.end && row.created_at && row.created_at > f.end + "\uffff") return false;
    if (f.search) {
      const needle = f.search.toLowerCase();
      const hay = `${row.action || ""} ${row.actor_name || ""} ${row.details || ""}`.toLowerCase();
      if (!hay.includes(needle)) return false;
    }
    return true;
  };

  // SSE: buffer newly-inserted rows in `pending`; user clicks the pill to
  // merge them into the on-screen list. Falls back silently when SSE isn't
  // available — the manual reload still works.
  useEffect(() => {
    const backend = process.env.REACT_APP_BACKEND_URL || "";
    if (typeof EventSource === "undefined") return;
    const es = new EventSource(`${backend}/api/activity-logs/stream`, { withCredentials: true });
    es.onmessage = (evt) => {
      try {
        const row = JSON.parse(evt.data);
        if (!matchesFilters(row)) return;
        setPending((prev) => {
          if (prev.some((r) => r.log_id === row.log_id)) return prev;
          return [row, ...prev].slice(0, 200);   // cap buffer
        });
      } catch { /* ignore malformed frame */ }
    };
    return () => es.close();
  }, []);

  const mergePending = () => {
    if (!pending.length) return;
    setLogs((prev) => {
      const seen = new Set(prev.map((r) => r.log_id));
      const additions = pending.filter((r) => !seen.has(r.log_id));
      return [...additions, ...prev];
    });
    setPending([]);
  };

  const visibleCount = 1 + COLS.filter((c) => isVisible(c.key)).length;
  const lpg = useTablePagination(logs, 20);
  const clearDates = () => { setStart(""); setEnd(""); };

  const fmt = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  const mondayOf = (d) => { const x = new Date(d); const dow = (x.getDay() + 6) % 7; x.setDate(x.getDate() - dow); return x; };
  const PRESETS = [
    { key: "today", label: "Today", range: () => { const t = new Date(); return [fmt(t), fmt(t)]; } },
    { key: "yesterday", label: "Yesterday", range: () => { const y = new Date(); y.setDate(y.getDate() - 1); return [fmt(y), fmt(y)]; } },
    { key: "thisweek", label: "This week", range: () => [fmt(mondayOf(new Date())), fmt(new Date())] },
    { key: "lastweek", label: "Last week", range: () => { const m = mondayOf(new Date()); const ls = new Date(m); ls.setDate(m.getDate() - 7); const le = new Date(m); le.setDate(m.getDate() - 1); return [fmt(ls), fmt(le)]; } },
    { key: "30d", label: "Last 30 days", range: () => { const s = new Date(); s.setDate(s.getDate() - 29); return [fmt(s), fmt(new Date())]; } },
  ];
  const applyPreset = (p) => { const [s, e] = p.range(); setStart(s); setEnd(e); };
  const activePreset = PRESETS.find((p) => { const [s, e] = p.range(); return s === start && e === end; })?.key;

  const exportCsv = () => {
    const vis = COLS.filter((c) => isVisible(c.key));
    const cell = (l, key) => {
      switch (key) {
        case "actor": return l.actor_name + (l.masqueraded_by_name ? ` (via ${l.masqueraded_by_name})` : "");
        case "category": return l.category || "";
        case "partner": return l.partner || "";
        case "customer": return l.customer || "";
        case "app": return l.app || "";
        case "details": return l.details || "";
        case "changes": return (l.changes || []).map((c) => `${c.field}: ${c.old} -> ${c.new}`).join("; ");
        case "time": return new Date(l.created_at).toLocaleString();
        default: return "";
      }
    };
    const header = ["Action", ...vis.map((c) => c.label)];
    const rows = logs.map((l) => [l.action, ...vis.map((c) => cell(l, c.key))]);
    const esc = (s) => `"${String(s).replace(/"/g, '""')}"`;
    const csv = [header, ...rows].map((r) => r.map(esc).join(",")).join("\n");
    downloadText(csv, `activity-logs-${fmt(new Date())}.csv`);
    toast.success(`Exported ${logs.length} log${logs.length === 1 ? "" : "s"}`);
  };

  return (
    <div>
      <PageHeader testid="activity-header"
                  title={canViewActivityLogs ? "Conduit Activity Log" : "System Logs"}
                  subtitle={canViewActivityLogs
                    ? "Login history, user changes, permission updates and app access."
                    : "Python warnings, unhandled exceptions, JS errors and OAuth/OIDC integration traces."} />

      {sections && canViewActivityLogs && (
      <CollapsibleSection title="Conduit Activity Log" subtitle="user actions, logins, permission changes" icon={ScrollText}
        open={sections.activity} onToggle={() => toggleSection("activity")} testid="activity-section" reportKey="activity_log" canReport={canReport}>
      <Card className="border-slate-200 shadow-sm" data-testid="activity-logs-card">
        {denied && (
          <div className="flex flex-wrap items-center gap-2 border-b border-amber-200 bg-amber-50 px-4 py-2.5 text-xs text-amber-900 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-200"
            data-testid="activity-logs-denied">
            <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
            <span>Your access to the activity log changed while this page was open, so there&rsquo;s
              nothing to show. Reload to pick up your current permissions.</span>
            <Button size="sm" variant="outline" className="h-6 px-2 text-xs"
              onClick={() => window.location.reload()} data-testid="activity-logs-denied-reload">
              Reload
            </Button>
          </div>
        )}
        {user?.base_role === "staff" && !has("activity_logs_all.view") && !isSuper && (
          <div className="border-b border-slate-100 bg-slate-50 px-4 py-2 text-xs text-slate-600 dark:border-slate-800 dark:bg-slate-800/50 dark:text-slate-300"
            data-testid="activity-scoped-note">
            Showing <b>your own activity only</b> — what you did, plus anything an admin did to your account.
            The whole platform&rsquo;s trail needs the &ldquo;Activity logs — everyone&rsquo;s&rdquo; permission.
          </div>
        )}
        <div className="flex flex-col gap-3 border-b border-slate-100 p-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input data-testid="logs-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search actions, actors…" className="pl-9" />
            </div>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger data-testid="logs-category-filter" className="w-full sm:w-44"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All categories</SelectItem>
                <SelectItem value="login">Login</SelectItem>
                <SelectItem value="account">Account</SelectItem>
                <SelectItem value="organization">Organization</SelectItem>
                <SelectItem value="permission">Permission</SelectItem>
                <SelectItem value="app">App</SelectItem>
                <SelectItem value="app_access">App Access</SelectItem>
                <SelectItem value="inventory">Inventory</SelectItem>
                <SelectItem value="quotes">Quotes</SelectItem>
                <SelectItem value="auth">Auth / Masquerade</SelectItem>
                <SelectItem value="settings">Settings</SelectItem>
                <SelectItem value="security">Security</SelectItem>
                <SelectItem value="kb">Knowledge Base</SelectItem>
              </SelectContent>
            </Select>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" data-testid="logs-columns-button" className="w-full sm:w-auto"><SlidersHorizontal className="h-4 w-4" /> Columns</Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuLabel>Show columns</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {COLS.map((c) => (
                  <DropdownMenuCheckboxItem key={c.key} checked={isVisible(c.key)} data-testid={`logs-col-toggle-${c.key}`}
                    onSelect={(e) => e.preventDefault()} onCheckedChange={() => toggleCol(c.key)}>
                    {c.label}
                  </DropdownMenuCheckboxItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            <Button variant="outline" data-testid="logs-export-button" onClick={exportCsv} className="w-full sm:w-auto"><Download className="h-4 w-4" /> Export CSV</Button>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs font-medium text-slate-500">Quick range</span>
            {PRESETS.map((p) => (
              <button key={p.key} data-testid={`logs-preset-${p.key}`} onClick={() => applyPreset(p)}
                className={`rounded-full border px-2.5 py-1 text-xs font-medium transition-colors ${activePreset === p.key ? "border-primary bg-primary/10 text-primary" : "border-slate-200 text-slate-500 hover:bg-slate-50"}`}>
                {p.label}
              </button>
            ))}
            <span className="mx-1 hidden h-4 w-px bg-slate-200 sm:inline-block" />
            <Input type="datetime-local" data-testid="logs-start-date" value={start} onChange={(e) => setStart(e.target.value)} className="h-8 w-56 text-xs" title="Start date & time" />
            <span className="text-xs text-slate-400">to</span>
            <Input type="datetime-local" data-testid="logs-end-date" value={end} onChange={(e) => setEnd(e.target.value)} className="h-8 w-56 text-xs" title="End date & time" />
            {(start || end) && (
              <button data-testid="logs-clear-dates" onClick={clearDates} className="inline-flex items-center gap-1 text-xs font-medium text-slate-500 hover:text-red-500">
                <X className="h-3.5 w-3.5" /> Clear
              </button>
            )}
            {/* Iter 115q — one-click view of rejected file uploads (SIP trace
                guard writes these under the `security` category). */}
            <span className="mx-1 hidden h-4 w-px bg-slate-200 sm:inline-block" />
            <button data-testid="logs-blocked-uploads"
              onClick={() => { setCategory("security"); setSearch("Rejected call trace upload"); }}
              className="inline-flex items-center gap-1 rounded-full border border-red-200 bg-red-50 px-2.5 py-1 text-xs font-medium text-red-700 transition-colors hover:bg-red-100">
              <ShieldAlert className="h-3.5 w-3.5" /> Blocked uploads
            </button>
            {(category !== "all" || search) && (
              <button data-testid="logs-clear-filters" onClick={() => { setCategory("all"); setSearch(""); }}
                className="inline-flex items-center gap-1 text-xs font-medium text-slate-500 hover:text-red-500">
                <X className="h-3.5 w-3.5" /> Reset filters
              </button>
            )}
          </div>
        </div>

        {pending.length > 0 && (
          <button
            type="button"
            onClick={mergePending}
            data-testid="logs-pending-pill"
            className="flex w-full items-center justify-center gap-2 border-b border-primary/20 bg-primary/5 px-5 py-2 text-xs font-semibold uppercase tracking-wide text-primary transition-colors hover:bg-primary/10"
          >
            <Sparkles className="h-3.5 w-3.5" />
            {pending.length} new event{pending.length === 1 ? "" : "s"} · click to show
          </button>
        )}

        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead>Action</TableHead>
              {isVisible("actor") && <TableHead>Actor</TableHead>}
              {isVisible("category") && <TableHead>Category</TableHead>}
              {isVisible("partner") && <TableHead>Partner</TableHead>}
              {isVisible("customer") && <TableHead>Customer</TableHead>}
              {isVisible("app") && <TableHead>App</TableHead>}
              {isVisible("details") && <TableHead>Details</TableHead>}
              {isVisible("changes") && <TableHead>Changes</TableHead>}
              {isVisible("time") && <TableHead>Time</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={visibleCount} className="py-10 text-center text-slate-400">Loading…</TableCell></TableRow>
            ) : logs.length === 0 ? (
              <TableRow><TableCell colSpan={visibleCount} className="py-10 text-center text-slate-400">No activity found</TableCell></TableRow>
            ) : (
              lpg.pageItems.map((l) => {
                const hasChanges = Array.isArray(l.changes) && l.changes.length > 0;
                const isOpen = !!expanded[l.log_id];
                return (
                  <Fragment key={l.log_id}>
                    <TableRow data-testid={`log-row-${l.log_id}`} className={hasChanges ? "cursor-pointer" : ""}
                      onClick={() => hasChanges && setExpanded((e) => ({ ...e, [l.log_id]: !e[l.log_id] }))}>
                      <TableCell className="py-3">
                        <div className="flex items-center gap-2.5">
                          <span className="flex h-7 w-7 items-center justify-center rounded-full bg-slate-100 text-slate-400">
                            {hasChanges ? <ChevronRight className={`h-4 w-4 transition-transform ${isOpen ? "rotate-90" : ""}`} /> : <ScrollText className="h-3.5 w-3.5" />}
                          </span>
                          <span className="text-sm font-medium text-slate-800">{l.action}</span>
                        </div>
                      </TableCell>
                      {isVisible("actor") && (
                        <TableCell className="text-sm text-slate-600">
                          {l.actor_name}
                          {l.masqueraded_by_name && (
                            <span data-testid={`log-masq-${l.log_id}`} className="ml-1.5 inline-flex items-center gap-1 rounded bg-amber-100 px-1.5 py-0.5 align-middle text-[10px] font-medium text-amber-800" title={`Acted via masquerade by ${l.masqueraded_by_name}`}>
                              <VenetianMask className="h-3 w-3" /> via {l.masqueraded_by_name}
                            </span>
                          )}
                        </TableCell>
                      )}
                      {isVisible("category") && <TableCell><span className={`rounded px-2 py-0.5 text-xs font-medium capitalize ${CAT_COLORS[l.category] || CAT_COLORS.general}`}>{CAT_LABELS[l.category] || (l.category || "general").replace("_", " ")}</span></TableCell>}
                      {isVisible("partner") && <TableCell className="text-sm text-slate-500">{l.partner || "—"}</TableCell>}
                      {isVisible("customer") && <TableCell className="text-sm text-slate-500">{l.customer || "—"}</TableCell>}
                      {isVisible("app") && <TableCell className="text-sm text-slate-500">{l.app || "—"}</TableCell>}
                      {isVisible("details") && <TableCell className="text-sm text-slate-500 max-w-[240px] truncate">{l.details || "—"}</TableCell>}
                      {isVisible("changes") && <TableCell className="text-sm">{hasChanges ? <span className="rounded bg-slate-100 px-1.5 py-0.5 text-xs font-medium text-slate-600">{l.changes.length} field{l.changes.length > 1 ? "s" : ""}</span> : <span className="text-slate-300">—</span>}</TableCell>}
                      {isVisible("time") && <TableCell className="text-xs text-slate-400 whitespace-nowrap">{new Date(l.created_at).toLocaleString()}</TableCell>}
                    </TableRow>
                    {hasChanges && isOpen && (
                      <TableRow key={`${l.log_id}-detail`} data-testid={`log-changes-${l.log_id}`} className="hover:bg-transparent">
                        <TableCell colSpan={visibleCount} className="bg-slate-50/70 py-3">
                          <div className="ml-10 overflow-hidden rounded-lg border border-slate-200 bg-white">
                            <table className="w-full text-sm">
                              <thead><tr className="border-b border-slate-100 text-left text-xs text-slate-400">
                                <th className="px-3 py-1.5 font-medium">Field</th><th className="px-3 py-1.5 font-medium">Before</th><th className="px-3 py-1.5 font-medium">After</th>
                              </tr></thead>
                              <tbody>
                                {l.changes.map((c, i) => (
                                  <tr key={i} className="border-b border-slate-50 last:border-0">
                                    <td className="px-3 py-1.5 font-medium text-slate-700">{c.field}</td>
                                    <td className="px-3 py-1.5 text-slate-400 line-through decoration-slate-300">{c.old}</td>
                                    <td className="px-3 py-1.5 font-medium text-emerald-700">{c.new}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </Fragment>
                );
              })
            )}
          </TableBody>
        </Table>
        <div className="flex items-center justify-between gap-3 border-t border-slate-100 px-3 py-2">
          <PageSizeSelect pageSize={lpg.pageSize} setPageSize={lpg.setPageSize} testid="logs-page-size" />
          <TablePagination {...lpg} testid="logs-pagination" />
        </div>
      </Card>
      </CollapsibleSection>
      )}

      {/* Iter 103 — System Logs (Python + Node.js/browser errors).
          Rendered only for users with `system_logs.view` permission
          (default: super_admin). Distinct from the user-activity
          table above, which tracks intentional user actions. */}
      {/* Iter 119v — Phone Portal Audit Log moved here from its own app tile. */}
      {sections && canViewAuditLog && (
        <CollapsibleSection title="Phone Portal Audit Log" subtitle="phone portal changes from the platform database" icon={Database}
          open={sections.audit} onToggle={() => toggleSection("audit")} testid="phone-portal-audit-section" reportKey="phone_audit_log" canReport={canReport}>
          <AuditLogPage />
        </CollapsibleSection>
      )}

      {sections && canViewRoleChanges && (
        <CollapsibleSection title="Role Change Log" subtitle="who changed whose role, and when" icon={UserCog}
          open={sections.role_changes} onToggle={() => toggleSection("role_changes")} testid="role-changes-section" reportKey="role_changes" canReport={canReport}>
          <RoleChangeLogPanel />
        </CollapsibleSection>
      )}

      {/* AWS CloudTrail — condensed read-only view of AWS management events.
          Needs BOTH `activity_logs.view` and `aws_cloudtrail.view`. */}
      {sections && canViewCloudTrail && (
        <CollapsibleSection title="AWS CloudTrail" subtitle="AWS management events · last 90 days" icon={Cloud}
          open={sections.aws_cloudtrail} onToggle={() => toggleSection("aws_cloudtrail")} testid="aws-cloudtrail-section" reportKey="aws_cloudtrail" canReport={canReport}>
          <AwsCloudTrailPanel />
        </CollapsibleSection>
      )}

      {sections && canViewSystemLogs && (
        <CollapsibleSection title="System Logs" subtitle="Python warnings, JS errors, OAuth/OIDC traces" icon={Terminal}
          open={sections.system} onToggle={() => toggleSection("system")} testid="system-section" reportKey="system_logs" canReport={canReport}>
          <SystemLogsPanel />
        </CollapsibleSection>
      )}

      {/* Iter 117 — OAuth Tracer moved here from the dashboard app.
          Super admins only. Polling only runs while expanded (the
          panel unmounts when collapsed). */}
      {sections && isSuper && (
        <CollapsibleSection title="OAuth Tracer" subtitle="live wire-level OAuth/OIDC feed · super admins only" icon={ActivityIcon}
          open={sections.oauth} onToggle={() => toggleSection("oauth")} testid="oauth-tracer-section"
          badge={<span className="rounded bg-cyan-50 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-cyan-700">live</span>}>
          <OAuthTracerPanel />
        </CollapsibleSection>
      )}

      {/* Iter 118 — AI Spend Guard: monthly Claude token/cost rollup per
          feature and per user + the priciest individual calls. */}
      {sections && isSuper && (
        <CollapsibleSection title="AI Spend Guard" subtitle="monthly Claude tokens & cost by feature and user · super admins only" icon={Wallet}
          open={sections.ai_spend} onToggle={() => toggleSection("ai_spend")} testid="ai-spend-section">
          <Card className="p-4"><AiSpendPanel /></Card>
        </CollapsibleSection>
      )}
    </div>
  );
}


/**
 * Iter 119x — Role Change Log.
 *
 * Every role edit is already captured in `audit_logs` as a `Role` diff row,
 * so this panel is a focused read over `/api/role-changes`: one line per
 * change with the actor, the person, and the before → after roles.
 * Scoped server-side (staff see all, partners see their own org tree).
 */
function RoleChangeLogPanel() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get("/role-changes", { params: { limit: 300 } })
      .then((r) => { if (alive) setRows(r.data || []); })
      .catch(() => { if (alive) setRows([]); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter((r) => `${r.target_name} ${r.target_email} ${r.actor_name} ${r.old_role} ${r.new_role} ${r.organization}`
      .toLowerCase().includes(needle));
  }, [rows, q]);
  const pg = useTablePagination(filtered, 20);

  const exportCsv = () => {
    const header = ["Time", "Actor", "User", "Email", "Organization", "From", "To", "IP"];
    const body = filtered.map((r) => [new Date(r.created_at).toLocaleString(), r.actor_name, r.target_name,
      r.target_email, r.organization, r.old_role, r.new_role, r.ip]);
    const esc = (s) => `"${String(s ?? "").replace(/"/g, '""')}"`;
    downloadText([header, ...body].map((x) => x.map(esc).join(",")).join("\n"), "role-changes.csv");
    toast.success(`Exported ${filtered.length} role change${filtered.length === 1 ? "" : "s"}`);
  };

  return (
    <Card className="border-slate-200 shadow-sm" data-testid="role-changes-card">
      <div className="flex flex-col gap-3 border-b border-slate-100 p-4 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input data-testid="role-changes-search" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search person, actor or role…" className="pl-9" />
        </div>
        <Button variant="outline" data-testid="role-changes-export" onClick={exportCsv} disabled={!filtered.length}>
          <Download className="h-4 w-4" /> Export CSV
        </Button>
      </div>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>User</TableHead>
            <TableHead>Role change</TableHead>
            <TableHead>Changed by</TableHead>
            <TableHead>Organization</TableHead>
            <TableHead>When</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {loading ? (
            <TableRow><TableCell colSpan={5} className="py-10 text-center text-sm text-slate-400">Loading…</TableCell></TableRow>
          ) : !filtered.length ? (
            <TableRow><TableCell colSpan={5} className="py-10 text-center text-sm text-slate-400" data-testid="role-changes-empty">
              No role changes recorded yet.
            </TableCell></TableRow>
          ) : (
            pg.pageItems.map((r) => (
              <TableRow key={r.log_id} data-testid={`role-change-row-${r.log_id}`}>
                <TableCell>
                  <div className="font-medium text-slate-800">
                    {r.target_name}
                    {r.target_deleted && <span className="ml-2 rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-500">deleted</span>}
                  </div>
                  {r.target_email && <div className="text-xs text-slate-400">{r.target_email}</div>}
                </TableCell>
                <TableCell>
                  <span className="inline-flex items-center gap-2 text-sm">
                    <span className="rounded bg-slate-100 px-1.5 py-0.5 text-xs text-slate-500 line-through decoration-slate-300">{r.old_role}</span>
                    <ArrowRight className="h-3.5 w-3.5 text-slate-300" />
                    <span className="rounded bg-emerald-50 px-1.5 py-0.5 text-xs font-medium text-emerald-700">{r.new_role}</span>
                  </span>
                </TableCell>
                <TableCell className="text-sm text-slate-600">
                  {r.actor_name}
                  {r.masqueraded_by_name && <span className="ml-1 text-xs text-amber-600">(via {r.masqueraded_by_name})</span>}
                </TableCell>
                <TableCell className="text-sm text-slate-500">{r.organization || "—"}</TableCell>
                <TableCell className="whitespace-nowrap text-xs text-slate-400">{new Date(r.created_at).toLocaleString()}</TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
      <div className="flex items-center justify-between gap-3 border-t border-slate-100 px-3 py-2">
        <PageSizeSelect pageSize={pg.pageSize} setPageSize={pg.setPageSize} testid="role-changes-page-size" />
        <TablePagination {...pg} testid="role-changes-pagination" />
      </div>
    </Card>
  );
}


/**
 * Iter 103 — System Logs panel.
 *
 * Search + filter table over the `system_logs` collection. Captures
 * both backend Python events (WARNING+, unhandled exceptions,
 * warnings.warn) and browser-side events (JS errors, unhandled promise
 * rejections, HTTP 5xx interceptor hits, React error boundaries).
 *
 * Only rendered for users with `system_logs.view` permission.
 */
const LEVEL_STYLE = {
  critical: "bg-red-100 text-red-800 border-red-200",
  error:    "bg-red-50 text-red-700 border-red-200",
  warning:  "bg-amber-50 text-amber-800 border-amber-200",
  info:     "bg-sky-50 text-sky-700 border-sky-200",
  debug:    "bg-slate-100 text-slate-500 border-slate-200",
};

function SystemLogsPanel() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [levels, setLevels] = useState({ critical: true, error: true, warning: true, info: true, debug: false });
  const [sourceKind, setSourceKind] = useState("all"); // all | backend | frontend
  const [since, setSince] = useState("24h");
  const [limit, setLimit] = useState(100);
  const [expanded, setExpanded] = useState(null); // row idx

  const selectedLevels = useMemo(
    () => Object.entries(levels).filter(([, v]) => v).map(([k]) => k).join(","),
    [levels]
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q.trim()) params.set("q", q.trim());
      if (selectedLevels) params.set("level", selectedLevels);
      if (sourceKind !== "all") params.set("source_kind", sourceKind);
      if (since) params.set("since", since);
      params.set("limit", String(limit));
      const { data } = await api.get(`/system-logs?${params.toString()}`);
      setRows(data.rows || []);
      setTotal(data.total || 0);
    } catch (e) {
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [q, selectedLevels, sourceKind, since, limit]);

  useEffect(() => { load(); }, [load]);

  // Iter 106f — 5-second auto-refresh toggle. Lets the operator watch a
  // live SSO attempt roll in without repeatedly clicking Refresh. Uses
  // a ref for the `load` function so the interval always calls the
  // latest closure (with the current filters) without re-arming when
  // deps change.
  const [autoRefresh, setAutoRefresh] = useState(false);
  const loadRef = useRef(load);
  useEffect(() => { loadRef.current = load; }, [load]);
  useEffect(() => {
    if (!autoRefresh) return undefined;
    const t = setInterval(() => { loadRef.current(); }, 5000);
    return () => clearInterval(t);
  }, [autoRefresh]);

  // Iter 106d — Export the current slice as a CSV file. Uses the same
  // filters as the visible table but pulls up to 500 rows so an
  // operator can attach a full SSO trace to a support email without
  // scrolling / screenshotting. Nested `context` is JSON-stringified
  // in its cell (secrets are already fingerprinted server-side).
  const [downloading, setDownloading] = useState(false);
  const downloadCsv = useCallback(async () => {
    setDownloading(true);
    try {
      const params = new URLSearchParams();
      if (q.trim()) params.set("q", q.trim());
      if (selectedLevels) params.set("level", selectedLevels);
      if (sourceKind !== "all") params.set("source_kind", sourceKind);
      if (since) params.set("since", since);
      params.set("limit", "500");
      const { data } = await api.get(`/system-logs?${params.toString()}`);
      const rowsData = data.rows || [];
      const HEADERS = [
        "ts", "level", "source_kind", "action", "status",
        "client_id", "user_id", "user_email",
        "method", "path", "referer", "user_agent",
        "source", "logger", "summary", "context_json",
      ];
      const esc = (v) => {
        if (v === null || v === undefined) return "";
        const s = typeof v === "object" ? JSON.stringify(v) : String(v);
        // RFC 4180: wrap in quotes, escape embedded quotes by doubling.
        return `"${s.replace(/"/g, '""')}"`;
      };
      const lines = [HEADERS.join(",")];
      for (const r of rowsData) {
        lines.push([
          esc(r.ts), esc(r.level), esc(r.source_kind || (r.logger === "frontend" ? "frontend" : "backend")),
          esc(r.action), esc(r.status),
          esc(r.client_id), esc(r.user_id), esc(r.user_email),
          esc(r.method), esc(r.path), esc(r.referer), esc(r.user_agent),
          esc(r.source), esc(r.logger), esc(r.summary),
          esc(r.context || {}),
        ].join(","));
      }
      // Excel-friendly: leading UTF-8 BOM so accented characters render.
      const csv = "\uFEFF" + lines.join("\r\n");
      const stamp = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
      const kind  = sourceKind === "all" ? "all" : sourceKind;
      downloadText(csv, `conduit-system-logs-${kind}-${stamp}.csv`, "text/csv;charset=utf-8");
    } catch (e) {
      toast.error("Download failed");
    } finally {
      setDownloading(false);
    }
  }, [q, selectedLevels, sourceKind, since]);

  return (
    <Card className="p-4 space-y-3" data-testid="system-logs-panel">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-xs text-slate-500">
            Python warnings, unhandled exceptions, browser-side JavaScript errors, and OAuth/OIDC integration traces (every message between an external app and Conduit&apos;s OIDC endpoints — secrets fingerprinted, never logged verbatim). Retained 30 days. Restricted to users with <code>system_logs.view</code>.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {/* Iter 106f — Live-tail toggle. Polls every 5s while ON so
              operators can watch an SSO attempt without clicking
              Refresh. Uses green when active (like a REC light) so
              it's obvious the panel is polling. */}
          <Button
            variant={autoRefresh ? "default" : "outline"}
            size="sm"
            onClick={() => setAutoRefresh((v) => !v)}
            data-testid="system-logs-autorefresh-toggle"
            aria-pressed={autoRefresh}
            className={autoRefresh ? "bg-emerald-600 text-white hover:bg-emerald-700" : ""}
          >
            {autoRefresh
              ? <><Pause className="h-3.5 w-3.5" /> Auto-refresh · 5s <span className="ml-1 inline-block h-1.5 w-1.5 animate-pulse rounded-full bg-white" /></>
              : <><Play className="h-3.5 w-3.5" /> Auto-refresh</>}
          </Button>
          <Button variant="outline" size="sm" onClick={downloadCsv} disabled={downloading || loading} data-testid="system-logs-download-csv">
            <Download className={`h-3.5 w-3.5 ${downloading ? "animate-pulse" : ""}`} />
            {sourceKind === "oauth" ? "Download OAuth CSV" : "Download CSV"}
          </Button>
          <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="system-logs-refresh">
            {loading ? <RefreshCcw className="h-3.5 w-3.5 animate-spin" /> : <RefreshCcw className="h-3.5 w-3.5" />}
            Refresh
          </Button>
        </div>
      </div>

      {/* Filter row */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative min-w-[240px] flex-1">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search summary, source, user or path…"
            className="h-9 pl-8 text-sm"
            data-testid="system-logs-search"
          />
        </div>
        <Select value={sourceKind} onValueChange={setSourceKind}>
          <SelectTrigger className="h-9 w-[170px]" data-testid="system-logs-source"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All sources</SelectItem>
            <SelectItem value="backend">Backend (Python)</SelectItem>
            <SelectItem value="frontend">Frontend (JS)</SelectItem>
            <SelectItem value="oauth">OAuth / OIDC</SelectItem>
          </SelectContent>
        </Select>
        <Select value={since} onValueChange={setSince}>
          <SelectTrigger className="h-9 w-[120px]" data-testid="system-logs-since"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="1h">Last hour</SelectItem>
            <SelectItem value="24h">Last 24h</SelectItem>
            <SelectItem value="7d">Last 7 days</SelectItem>
            <SelectItem value="30d">Last 30 days</SelectItem>
          </SelectContent>
        </Select>
        <Select value={String(limit)} onValueChange={(v) => setLimit(Number(v))}>
          <SelectTrigger className="h-9 w-[110px]" data-testid="system-logs-limit"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="50">50 rows</SelectItem>
            <SelectItem value="100">100 rows</SelectItem>
            <SelectItem value="250">250 rows</SelectItem>
            <SelectItem value="500">500 rows</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Level toggles */}
      <div className="flex flex-wrap items-center gap-1.5 rounded-lg border border-slate-200 bg-slate-50 px-2 py-1.5">
        <span className="text-[11px] font-medium uppercase tracking-wider text-slate-500">Levels:</span>
        {["critical", "error", "warning", "info", "debug"].map((lv) => (
          <button
            key={lv}
            type="button"
            onClick={() => setLevels((L) => ({ ...L, [lv]: !L[lv] }))}
            data-testid={`system-logs-lv-${lv}`}
            className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium transition ${
              levels[lv] ? LEVEL_STYLE[lv] : "border-slate-200 bg-white text-slate-400"
            }`}>
            {lv}
          </button>
        ))}
      </div>

      <div className="rounded-lg border border-slate-200 overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[150px]">When</TableHead>
              <TableHead className="w-[80px]">Level</TableHead>
              <TableHead className="w-[80px]">Source</TableHead>
              <TableHead className="w-[180px]">User</TableHead>
              <TableHead>Summary</TableHead>
              <TableHead className="w-[180px]">Where</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && (
              <TableRow><TableCell colSpan={6} className="text-center text-sm text-slate-500 py-8">Loading…</TableCell></TableRow>
            )}
            {!loading && rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} className="text-center text-sm text-slate-500 py-8">
                  <AlertTriangle className="mx-auto h-6 w-6 text-slate-300" />
                  <div className="mt-2">No system logs match these filters.</div>
                </TableCell>
              </TableRow>
            )}
            {!loading && rows.map((r, idx) => {
              const isFrontend = r.source_kind === "frontend" || r.logger === "frontend";
              const isOAuth = r.source_kind === "oauth";
              const isOpen = expanded === idx;
              return (
                <Fragment key={idx}>
                  <TableRow
                    className="cursor-pointer hover:bg-slate-50"
                    onClick={() => setExpanded(isOpen ? null : idx)}
                    data-testid={`system-log-row-${idx}`}>
                    <TableCell className="whitespace-nowrap text-xs text-slate-500">{fmtTs(r.ts)}</TableCell>
                    <TableCell>
                      <span className={`inline-flex rounded-full border px-2 py-0.5 text-[10px] font-semibold ${LEVEL_STYLE[r.level] || LEVEL_STYLE.info}`}>
                        {r.level}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-medium ${
                        isOAuth ? "bg-indigo-50 text-indigo-700"
                                : isFrontend ? "bg-fuchsia-50 text-fuchsia-700"
                                             : "bg-teal-50 text-teal-700"
                      }`}>
                        {isOAuth ? "oauth" : (isFrontend ? "frontend" : "backend")}
                      </span>
                    </TableCell>
                    <TableCell className="text-xs text-slate-700">
                      {r.user_email || <span className="italic text-slate-400">anonymous</span>}
                    </TableCell>
                    <TableCell className="text-xs text-slate-800 max-w-[520px] truncate" title={r.summary}>{r.summary}</TableCell>
                    <TableCell className="text-xs text-slate-500 max-w-[180px] truncate" title={r.referer || r.path || ""}>{r.referer || r.path || "—"}</TableCell>
                  </TableRow>
                  {isOpen && (
                    <TableRow className="bg-slate-50">
                      <TableCell colSpan={6} className="text-xs">
                        <div className="grid grid-cols-1 gap-1 sm:grid-cols-2">
                          <div><span className="font-semibold text-slate-600">Logger:</span> <code>{r.logger || "—"}</code></div>
                          <div><span className="font-semibold text-slate-600">Source:</span> <code>{r.source || "—"}</code></div>
                          <div><span className="font-semibold text-slate-600">Path:</span> <code>{r.path || "—"}</code></div>
                          <div><span className="font-semibold text-slate-600">Method:</span> <code>{r.method || "—"}</code></div>
                          <div className="sm:col-span-2"><span className="font-semibold text-slate-600">Referer:</span> <code className="break-all">{r.referer || "—"}</code></div>
                          <div className="sm:col-span-2"><span className="font-semibold text-slate-600">Full summary:</span> <code className="break-all">{r.summary}</code></div>
                          {/* Iter 106 — Additional fields surfaced only
                              for OAuth/OIDC trace rows. Lets ops see
                              the client, action and every non-secret
                              parameter that was exchanged. */}
                          {isOAuth && (
                            <>
                              <div><span className="font-semibold text-slate-600">Client:</span> <code>{r.client_id || "—"}</code></div>
                              <div><span className="font-semibold text-slate-600">Action:</span> <code>{r.action || "—"}</code></div>
                              <div><span className="font-semibold text-slate-600">Status:</span> <code>{r.status || "—"}</code></div>
                              <div><span className="font-semibold text-slate-600">User Agent:</span> <code className="break-all">{r.user_agent || "—"}</code></div>
                              {r.context && Object.keys(r.context).length > 0 && (
                                <div className="sm:col-span-2 mt-1">
                                  <span className="font-semibold text-slate-600">Context (secrets fingerprinted):</span>
                                  <pre className="mt-1 max-h-64 overflow-auto rounded bg-slate-900/90 p-2 font-mono text-[11px] leading-snug text-emerald-100" data-testid={`system-log-oauth-context-${idx}`}>
{JSON.stringify(r.context, null, 2)}
                                  </pre>
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                </Fragment>
              );
            })}
          </TableBody>
        </Table>
      </div>
      <div className="text-xs text-slate-500 flex items-center justify-between">
        <span data-testid="system-logs-count">{rows.length} of {total} matching rows</span>
        <span className="text-slate-400">Retention 30 days</span>
      </div>
    </Card>
  );
}

function fmtTs(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { hour12: false });
  } catch { return iso; }
}

